// src/pages/GestionLotes/GestionLotes.js
//
// Página unificada con tabs para Readjudicación Masiva y Carga Masiva.
// Reemplaza el submenú del sidebar por un tab switcher que cambia la URL
// (para deep-linking) sin remontar el sidebar.
//
// Rutas manejadas:
//   /gestion-lotes                  → tab por defecto según permisos
//   /gestion-lotes?tab=readjudicacion
//   /gestion-lotes?tab=carga-masiva
//
// Las rutas legacy /readjudicacion-masiva y /carga-masiva siguen funcionando
// para compatibilidad (pantallas de detalle y enlaces antiguos).

import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Tabs, Tab, Paper } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { usePermisos } from '../../utils/usePermisos';
import ReadjudicacionMasiva from '../ReadjudicacionMasiva/ReadjudicacionMasiva';
import CargaMasiva from '../CargaMasiva/CargaMasiva';

const TAB_READJUDICACION = 'readjudicacion';
const TAB_CARGA_MASIVA = 'carga-masiva';

export default function GestionLotes() {
    const { tienePermiso } = usePermisos();
    const [searchParams, setSearchParams] = useSearchParams();

    const puedeVerReadj = tienePermiso('READJUDICACION MASIVA', 'SELECT');
    const puedeVerCarga = tienePermiso('CARGA MASIVA', 'SELECT');

    const tabs = useMemo(() => {
        const t = [];
        if (puedeVerCarga) t.push({ value: TAB_CARGA_MASIVA, label: 'Carga Masiva', icon: <UploadFileIcon /> });
        if (puedeVerReadj) t.push({ value: TAB_READJUDICACION, label: 'Readjudicación Masiva', icon: <GavelIcon /> });
        return t;
    }, [puedeVerReadj, puedeVerCarga]);

    // Tab activa: primero por query param, sino el primero permitido
    const tabActiva = useMemo(() => {
        const requested = searchParams.get('tab');
        if (requested && tabs.some(t => t.value === requested)) return requested;
        return tabs[0]?.value || null;
    }, [searchParams, tabs]);

    const handleChange = (_, newValue) => {
        setSearchParams({ tab: newValue }, { replace: true });
    };

    if (tabs.length === 0) {
        return (
            <Box sx={{ p: 4 }}>
                <Paper sx={{ p: 3, borderRadius: '4px' }}>
                    No tenés permisos para ver módulos de Gestión de Lotes.
                </Paper>
            </Box>
        );
    }

    return (
        <Box>
            <Paper elevation={0} sx={{ borderRadius: 0, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                <Tabs
                    value={tabActiva}
                    onChange={handleChange}
                    variant="standard"
                    sx={{
                        px: 2,
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 56 },
                    }}
                >
                    {tabs.map(t => (
                        <Tab
                            key={t.value}
                            value={t.value}
                            label={t.label}
                            icon={t.icon}
                            iconPosition="start"
                        />
                    ))}
                </Tabs>
            </Paper>

            <Box>
                {tabActiva === TAB_READJUDICACION && <ReadjudicacionMasiva />}
                {tabActiva === TAB_CARGA_MASIVA && <CargaMasiva />}
            </Box>
        </Box>
    );
}
